<template>
  <aside class="workspace-sidebar" :class="{ 'workspace-sidebar--mobile': props.mobile }">
    <div class="sidebar-header">
      <a
        href="https://huiben.yunzhan365.com"
        class="sidebar-logo"
        aria-label="返回官网首页"
        @click="handleNavigate"
      >
        <SvgIcon name="logo" class="logo-mark" />
      </a>

      <button
        v-if="props.mobile"
        type="button"
        class="sidebar-close-button"
        aria-label="关闭工作台侧边栏"
        @click="handleClose"
      >
        <SvgIcon name="cross" :size="10" />
      </button>
    </div>

    <nav class="sidebar-menu" aria-label="工作台主菜单">
      <router-link
        v-for="item in menuItems"
        :key="item.name"
        :to="{ name: item.name }"
        class="sidebar-menu-item"
        :class="{ active: currentRouteName === item.name && !isSettingsRoute }"
        @click="handleNavigate"
      >
        <el-icon class="menu-icon" :size="18">
          <component :is="item.icon" />
        </el-icon>
        <span>{{ item.label }}</span>
      </router-link>
    </nav>

    <button
      type="button"
      class="sidebar-watermark-btn"
      @click="handleOpenWatermark"
    >
      <SvgIcon name="sidebar-watermark" :size="18" />
      <span>AI生成水印设置</span>
    </button>
  </aside>
</template>

<script setup lang="ts">
  import { computed, type Component } from 'vue';
  import { useRoute } from 'vue-router';
  import { Collection, MagicStick, User } from '@element-plus/icons-vue';
  import SvgIcon from '@/components/common/icon/SvgIcon.vue';
  import { emitter } from '@/utils';
  import { EVENT_NAMES } from '@/constants';

  interface Props {
    mobile?: boolean;
  }

  interface WorkspaceMenuItem {
    name: string;
    label: string;
    icon: Component;
  }

  const props = withDefaults(defineProps<Props>(), {
    mobile: false
  });

  const emit = defineEmits<{
    close: [];
    navigate: [];
  }>();

  const route = useRoute();
  const currentRouteName = computed(() => route.name as string);

  const settingsRouteNames = ['settings-profile', 'settings-account'];
  const isSettingsRoute = computed(() => settingsRouteNames.includes(currentRouteName.value));

  const menuItems: WorkspaceMenuItem[] = [
    { name: 'create', label: '一键创作', icon: MagicStick },
    { name: 'character', label: '设计角色', icon: User },
    { name: 'story', label: '我的故事', icon: Collection }
  ];

  function handleClose() {
    emit('close');
  }

  function handleNavigate() {
    emit('navigate');
  }

  function handleOpenWatermark() {
    // mitt 要求事件 payload 显式传入（即使为空对象）
    emitter.emit(EVENT_NAMES.WATERMARK_DIALOG_OPEN, {});
  }
</script>

<style scoped>
  .workspace-sidebar {
    width: 240px;
    height: 100%;
    padding: 28px 20px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 20px;
    background: #fefdff;
    border-radius: 16px;
    box-shadow:
      0 4px 20px rgba(0, 0, 0, 0.08),
      0 0 1px rgba(0, 0, 0, 0.1);
  }

  .workspace-sidebar--mobile {
    width: 227px;
    padding: 18px 13px 28px 6px;
    border-radius: 0;
    gap: 24px;
  }

  .sidebar-header {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }

  .sidebar-logo {
    width: fit-content;
    height: 28px;
    padding: 0 12px;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: #160211;
    text-decoration: none;
  }

  .workspace-sidebar--mobile .sidebar-logo {
    padding: 0;
  }

  .sidebar-close-button {
    width: 19px;
    height: 16px;
    padding: 2px;
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: #333333;
    cursor: pointer;
    flex-shrink: 0;
  }

  .logo-mark {
    width: 131px;
    height: 28px;
    flex-shrink: 0;
  }

  .sidebar-menu {
    width: 200px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .workspace-sidebar--mobile .sidebar-menu {
    margin-left: 6px;
  }

  .sidebar-menu-item {
    width: 200px;
    height: 36px;
    padding: 0 12px;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    gap: 8px;
    border-radius: 16px;
    color: #160211;
    font-size: 14px;
    line-height: 22px;
    text-decoration: none;
    transition:
      background 0.15s,
      color 0.15s;
  }

  .sidebar-menu-item:hover,
  .sidebar-menu-item.active {
    background: #ede8ff;
    color: #6033e1;
  }

  .menu-icon {
    flex-shrink: 0;
  }

  .sidebar-watermark-btn {
    width: 200px;
    height: 38px;
    margin-top: auto;
    padding: 0;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 8px;
    background: transparent;
    color: #160211;
    font-size: 14px;
    line-height: 14px;
    cursor: pointer;
    transition:
      background 0.15s,
      color 0.15s;
  }

  .sidebar-watermark-btn:hover {
    background: #ede8ff;
    color: #6033e1;
  }
</style>
